import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import { ModalBase } from '@components/Modal';
import Overline from '@components/Overline';
import OutlinedIconButton from '@components/OutlinedIconButton';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import Button from '@components/Button';
import { AIAssistantLoadingWrapper } from '@features/AIAssistant';
import { useTranslation } from '@hooks';
import { LOCALIZE_MODAL_LANGUAGES } from '@constants/microsites';
import { MENU_TEXT_LIMITS } from '@constants/menus';
import DescriptionAssistant from './DescriptionAssistant';

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        outline: 'none',
        width: '100%',
        height: 560,
        maxWidth: 652,
    },
    '& .modal-base-content': {
        padding: 16,
        overflow: 'auto',
    },
});

const Row = styled('div', {
    shouldForwardProp: prop => prop !== 'withDelete',
})(({ withDelete }) => ({
    display: 'grid',
    gridGap: 16,
    gridTemplateColumns: withDelete ? '160px 1fr 40px' : '160px 1fr',
    marginBottom: 16,
    '@media (max-width: 600px)': {
        gridTemplateColumns: '100%',
        gridGap: 8,
    },
}));

const buildRows = (names, descriptions, options, defaultLanguage) => {
    const languages = [
        ...new Set([
            defaultLanguage,
            ...names.map(n => n.language),
            ...descriptions.map(d => d.language),
            ...(options || []).flatMap(option => (option.names || []).map(n => n.language)),
        ]),
    ];
    return languages.map(language => ({
        language,
        name: names.find(n => n.language === language)?.value || '',
        description: descriptions.find(d => d.language === language)?.value || '',
        optionNames: Object.fromEntries(
            (options || []).map(option => [
                option.id,
                (option.names || []).find(n => n.language === language)?.value || '',
            ]),
        ),
    }));
};

// Edits the localized name/description pairs of one menu node (menu, section or item),
// including the item's option names when options are passed.
const MenuLocalizeModal = ({
    isOpen,
    title,
    names,
    descriptions,
    options = null,
    aiContext = null,
    defaultLanguage,
    onClose,
    onSave,
}) => {
    const [rows, setRows] = useState(() => buildRows(names, descriptions, options, defaultLanguage));
    const [nameTouched, setNameTouched] = useState(false);
    const [aiLoadingRow, setAiLoadingRow] = useState(null);

    const account = useSelector(state => state.account?.account);
    const { t } = useTranslation();

    const languageOptions = LOCALIZE_MODAL_LANGUAGES[account?.locale] || [];

    const unusedLanguages = useMemo(
        () => languageOptions.filter(lang => !rows.some(r => r.language === lang.value)),
        [languageOptions, rows],
    );

    const onChangeRow = (index, field, value) => {
        if (index === 0 && field === 'name') setNameTouched(true);
        setRows(prev => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    };

    const onChangeOptionName = (index, optionId, value) => {
        setRows(prev =>
            prev.map((row, i) =>
                i === index ? { ...row, optionNames: { ...row.optionNames, [optionId]: value } } : row,
            ),
        );
    };

    const onAddLanguage = () => {
        setRows(prev => [...prev, { language: unusedLanguages[0].value, name: '', description: '', optionNames: {} }]);
    };

    const onDeleteRow = index => {
        setRows(prev => prev.filter((_, i) => i !== index));
    };

    const handleSave = () => {
        const toTexts = field =>
            rows.filter(row => row[field]).map(row => ({ language: row.language, value: row[field] }));
        const updatedOptions = options
            ? options.map(option => ({
                  ...option,
                  names: rows
                      .filter(row => row.optionNames?.[option.id])
                      .map(row => ({ language: row.language, value: row.optionNames[option.id] })),
              }))
            : null;
        onSave(toTexts('name'), toTexts('description'), updatedOptions);
    };

    const issues = [];
    if (!rows[0]?.name?.trim()) issues.push(t('MenusPage.issueName'));
    if (rows.some(row => (row.name || '').length > MENU_TEXT_LIMITS.name)) {
        issues.push(t('MenusPage.issueNameLength'));
    }
    if (rows.some(row => (row.description || '').length > MENU_TEXT_LIMITS.description)) {
        issues.push(t('MenusPage.issueDescriptionLength'));
    }
    if ((options || []).some(option => !rows[0]?.optionNames?.[option.id]?.trim())) {
        issues.push(t('MenusPage.issueOptionNames'));
    }
    if (
        rows.some(row =>
            Object.values(row.optionNames || {}).some(value => (value || '').length > MENU_TEXT_LIMITS.name),
        )
    ) {
        issues.push(t('MenusPage.issueNameLength'));
    }
    const isValid = issues.length === 0;

    const renderRow = (row, index) => {
        const selectedLanguage = languageOptions.find(l => l.value === row.language);
        const langOptions = [...(selectedLanguage ? [selectedLanguage] : []), ...unusedLanguages];
        const isDefault = index === 0;
        return (
            <Row key={`${row.language}-${index}`} withDelete={!isDefault}>
                <Select
                    value={row.language}
                    onChange={e => onChangeRow(index, 'language', e.target.value)}
                    label={t('common.language')}
                    disabled={isDefault}
                    fullWidth
                    options={langOptions}
                />
                <Input
                    inputProps={{
                        value: row.name,
                        onChange: e => onChangeRow(index, 'name', e.target.value),
                    }}
                    label={isDefault ? t('common.title') : t('MenusPage.titleOptional')}
                    required={isDefault}
                    error={isDefault && nameTouched && !row.name?.trim()}
                    fullWidth
                />
                {!isDefault ? (
                    <OutlinedIconButton onClick={() => onDeleteRow(index)}>
                        <DeleteIcon />
                    </OutlinedIconButton>
                ) : null}
                <div style={{ position: 'relative', gridColumn: '1 / -1' }}>
                    {aiLoadingRow === index && <AIAssistantLoadingWrapper />}
                    <Input
                        inputProps={{
                            value: row.description,
                            onChange: e => onChangeRow(index, 'description', e.target.value),
                        }}
                        label={t('common.description')}
                        fullWidth
                        multiline
                        minRows={2}
                        error={(row.description || '').length > MENU_TEXT_LIMITS.description}
                        helperText={
                            (row.description || '').length > MENU_TEXT_LIMITS.description
                                ? t('MenusPage.issueDescriptionLength')
                                : ''
                        }
                        disabled={aiLoadingRow === index}
                        toolbar={
                            aiContext ? (
                                <DescriptionAssistant
                                    value={row.description}
                                    setValue={newValue => onChangeRow(index, 'description', newValue)}
                                    context={aiContext}
                                    language={row.language}
                                    disabled={aiLoadingRow !== null}
                                    setLoading={loading => setAiLoadingRow(loading ? index : null)}
                                />
                            ) : undefined
                        }
                    />
                </div>
                {(options || []).map((option, optionIndex) => (
                    <Input
                        key={option.id}
                        inputProps={{
                            value: row.optionNames?.[option.id] || '',
                            onChange: e => onChangeOptionName(index, option.id, e.target.value),
                        }}
                        label={`${t('MenusPage.option')} ${optionIndex + 1}`}
                        required={isDefault}
                        error={isDefault && !row.optionNames?.[option.id]?.trim()}
                        fullWidth
                        sx={{ gridColumn: '1 / -1' }}
                    />
                ))}
            </Row>
        );
    };

    return (
        <Modal
            fullScreen
            isOpen={isOpen}
            handleClose={onClose}
            title={title}
            onPrimaryAction={handleSave}
            primaryActionDisabled={!isValid}
            primaryActionTooltip={
                issues.length ? (
                    <div>
                        {issues.map(issue => (
                            <div key={issue}>• {issue}</div>
                        ))}
                    </div>
                ) : (
                    ''
                )
            }
            primaryActionText={t('buttons.save')}
        >
            <div>
                <Overline variant="caption" sx={{ marginBottom: '16px' }}>
                    {t('LocalizeItemModal.defaultLanguages')}
                </Overline>
                {renderRow(rows[0], 0)}
                <div
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}
                >
                    <Overline variant="caption">{t('LocalizeItemModal.additionalLanguages')}</Overline>
                    <Button variant="outlined" onClick={onAddLanguage} disabled={!unusedLanguages.length}>
                        {t('buttons.addLanguage')}
                    </Button>
                </div>
                {rows.slice(1).map((row, index) => renderRow(row, index + 1))}
            </div>
        </Modal>
    );
};

export default MenuLocalizeModal;
